import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  IconButton,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { sizeSchemaApi, sizeCurveApi } from '../../services/api';

function CurvesPanel({ schemaKey, curves, onChange }) {
  const [adding, setAdding] = useState(false);
  const [curveName, setCurveName] = useState('');
  const [orderedSizes, setOrderedSizes] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    try {
      setSaving(true);
      setError(null);
      const sizes = orderedSizes.split(',').map(s => s.trim()).filter(Boolean);
      if (sizes.length === 0) {
        setError('At least one size required');
        return;
      }
      await sizeCurveApi.create({
        schema_key: schemaKey,
        curve_name: curveName,
        ordered_sizes: sizes,
      });
      setAdding(false);
      setCurveName('');
      setOrderedSizes('');
      await onChange();
    } catch (e) {
      setError(e.message || 'Failed to add curve');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (curveId) => {
    if (!window.confirm('Delete this curve?')) return;
    try {
      setError(null);
      await sizeCurveApi.delete(curveId);
      await onChange();
    } catch (e) {
      setError(e.message || 'Failed to delete');
    }
  };

  return (
    <Box sx={{ p: 2, bgcolor: 'grey.100', borderTop: 1, borderColor: 'divider' }}>
      <Typography variant="subtitle2" gutterBottom>
        Curves for this schema
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      {curves.length === 0 && !adding && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          No curves defined.
        </Typography>
      )}
      <Stack spacing={1}>
        {curves.map(c => (
          <Box key={c.id} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip label={c.curve_name} size="small" color={c.active ? 'primary' : 'default'} variant={c.active ? 'filled' : 'outlined'} />
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
              {(c.ordered_sizes || []).map((sz, i) => (
                <Chip key={`${c.id}-${i}`} label={sz} size="small" variant="outlined" />
              ))}
            </Stack>
            <IconButton size="small" onClick={() => handleDelete(c.id)}><DeleteIcon fontSize="small" /></IconButton>
          </Box>
        ))}
      </Stack>
      {adding ? (
        <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: 'center' }}>
          <TextField label="Curve name" size="small" value={curveName} onChange={(e) => setCurveName(e.target.value)} sx={{ width: 160 }} />
          <TextField
            label="Ordered sizes (comma-separated, lowest to highest)"
            size="small"
            value={orderedSizes}
            onChange={(e) => setOrderedSizes(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Button size="small" variant="contained" onClick={handleAdd} disabled={saving || !curveName || !orderedSizes}>
            {saving ? 'Saving...' : 'Add'}
          </Button>
          <Button size="small" onClick={() => { setAdding(false); setCurveName(''); setOrderedSizes(''); }}>Cancel</Button>
        </Stack>
      ) : (
        <Button size="small" startIcon={<AddIcon />} onClick={() => setAdding(true)} sx={{ mt: 1 }}>
          Add curve
        </Button>
      )}
    </Box>
  );
}

function FamiliesPanel({ schemaKey, families, allFamilies, onChange, onReclassify }) {
  const [adding, setAdding] = useState(false);
  const [familyId, setFamilyId] = useState('');
  const [priority, setPriority] = useState(5);
  const [error, setError] = useState(null);

  const remaining = allFamilies.filter(f => !families.some(fam => fam.family_id === f.id));

  const handleAdd = async () => {
    try {
      setError(null);
      await sizeSchemaApi.addFamily(schemaKey, familyId, Number(priority));
      setAdding(false);
      setFamilyId('');
      setPriority(5);
      await onChange();
      if (onReclassify) await onReclassify();
    } catch (e) {
      setError(e.message || 'Failed to add');
    }
  };

  const handleRemove = async (fid) => {
    try {
      setError(null);
      await sizeSchemaApi.removeFamily(schemaKey, fid);
      await onChange();
      if (onReclassify) await onReclassify();
    } catch (e) {
      setError(e.message || 'Failed to remove');
    }
  };

  const handleUpdatePriority = async (fid, newPriority) => {
    try {
      setError(null);
      await sizeSchemaApi.updateFamily(schemaKey, fid, { priority: Number(newPriority) });
      await onChange();
      if (onReclassify) await onReclassify();
    } catch (e) {
      setError(e.message || 'Failed to update priority');
    }
  };

  return (
    <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
      <Typography variant="subtitle2" gutterBottom>
        Applicable category families (priority resolves overlap when multiple schemas match)
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Family</TableCell>
            <TableCell width={120}>Priority</TableCell>
            <TableCell>Notes</TableCell>
            <TableCell align="right" width={60} />
          </TableRow>
        </TableHead>
        <TableBody>
          {families.length === 0 && (
            <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary">No families mapped yet.</Typography></TableCell></TableRow>
          )}
          {families.map(f => (
            <TableRow key={f.family_id}>
              <TableCell><Chip label={f.family_name} size="small" /></TableCell>
              <TableCell>
                <TextField
                  size="small"
                  type="number"
                  defaultValue={f.priority}
                  onBlur={(e) => {
                    if (Number(e.target.value) !== f.priority) handleUpdatePriority(f.family_id, e.target.value);
                  }}
                  sx={{ width: 80 }}
                />
              </TableCell>
              <TableCell><Typography variant="caption" color="text.secondary">{f.notes || '-'}</Typography></TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => handleRemove(f.family_id)}><DeleteIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {adding ? (
        <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Family</InputLabel>
            <Select value={familyId} label="Family" onChange={(e) => setFamilyId(e.target.value)}>
              {remaining.map(f => <MenuItem key={f.id} value={f.id}>{f.family}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Priority" type="number" size="small" value={priority} onChange={(e) => setPriority(e.target.value)} sx={{ width: 100 }} />
          <Button size="small" variant="contained" onClick={handleAdd} disabled={!familyId}>Add</Button>
          <Button size="small" onClick={() => { setAdding(false); setFamilyId(''); }}>Cancel</Button>
        </Stack>
      ) : (
        <Button size="small" startIcon={<AddIcon />} onClick={() => setAdding(true)} sx={{ mt: 2 }} disabled={remaining.length === 0}>
          Add family mapping
        </Button>
      )}
    </Box>
  );
}

export default function SizeSchemasDashboard() {
  const [schemas, setSchemas] = useState([]);
  const [families, setFamilies] = useState([]);
  const [curves, setCurves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ schema_key: '', label: '', description: '', example_sizes: '' });
  const [saving, setSaving] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [schemaResp, famResp, curveResp] = await Promise.all([
        sizeSchemaApi.list(),
        sizeSchemaApi.listCategoryFamilies(),
        sizeCurveApi.list(),
      ]);
      setSchemas(schemaResp.schemas || []);
      setFamilies(famResp.families || []);
      setCurves(curveResp.curves || []);
    } catch (e) {
      setError(e.message || 'Failed to load schemas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleOpenAdd = () => {
    setEditing(null);
    setFormData({ schema_key: '', label: '', description: '', example_sizes: '' });
    setDialogOpen(true);
  };

  const handleOpenEdit = (s) => {
    setEditing(s);
    setFormData({
      schema_key: s.schema_key,
      label: s.label,
      description: s.description || '',
      example_sizes: (s.example_sizes || []).join(', '),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const example_sizes = formData.example_sizes
        ? formData.example_sizes.split(',').map(s => s.trim()).filter(Boolean)
        : [];
      if (editing) {
        await sizeSchemaApi.update(editing.schema_key, {
          label: formData.label,
          description: formData.description,
          example_sizes,
        });
      } else {
        await sizeSchemaApi.create({
          schema_key: formData.schema_key,
          label: formData.label,
          description: formData.description,
          example_sizes,
        });
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (s) => {
    try {
      await sizeSchemaApi.update(s.schema_key, { active: !s.active });
      await load();
    } catch (e) {
      setError(e.message || 'Failed to toggle');
    }
  };

  const handleReclassify = async () => {
    try {
      setReclassifying(true);
      setInfo(null);
      const result = await sizeSchemaApi.reclassify();
      const stats = result.result?.stats || {};
      setInfo(`Reclassified ${(stats.rows_processed || 0).toLocaleString()} rows in ${Math.round(stats.duration_seconds || 0)}s`);
    } catch (e) {
      setError(e.message || 'Reclassification failed');
    } finally {
      setReclassifying(false);
    }
  };

  // Lighter helper for chained auto-reclassify after family/schema edits — doesn't toggle the
  // top-level button spinner, just shows a result toast.
  const triggerReclassify = async () => {
    try {
      const result = await sizeSchemaApi.reclassify();
      const stats = result.result?.stats || {};
      setInfo(`Reclassified ${(stats.rows_processed || 0).toLocaleString()} rows in ${Math.round(stats.duration_seconds || 0)}s`);
    } catch (e) {
      setError(`Save succeeded, but reclassify failed: ${e.message || e}`);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5">Size Schemas</Typography>
          <Typography variant="body2" color="text.secondary">
            Canonical size encoding types. The classifier picks one schema per size based on
            pattern + applicable category family. Changes take effect on the next pipeline run
            (or use Reclassify to apply now).
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleReclassify} disabled={reclassifying}>
            {reclassifying ? 'Reclassifying...' : 'Reclassify Unknown Sizes'}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>Add Schema</Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell><strong>Key</strong></TableCell>
              <TableCell><strong>Label</strong></TableCell>
              <TableCell><strong>Examples</strong></TableCell>
              <TableCell><strong>Applicable families (priority)</strong></TableCell>
              <TableCell align="center"><strong>Active</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schemas.map(s => {
              const schemaCurves = curves.filter(c => c.schema_key === s.schema_key);
              return (
              <React.Fragment key={s.schema_key}>
                <TableRow sx={{ opacity: s.active ? 1 : 0.5 }}>
                  <TableCell>
                    <IconButton size="small" onClick={() => setExpanded({ ...expanded, [s.schema_key]: !expanded[s.schema_key] })}>
                      {expanded[s.schema_key] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell><Chip label={s.schema_key} size="small" color="primary" /></TableCell>
                  <TableCell>{s.label}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(s.example_sizes || []).slice(0, 6).map(ex => (
                        <Chip key={ex} label={ex} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(s.applicable_families || []).length === 0 ? (
                        <Typography variant="caption" color="text.secondary">none</Typography>
                      ) : (
                        s.applicable_families.map(f => (
                          <Chip
                            key={f.family_id}
                            label={`${f.family_name} (p${f.priority})`}
                            size="small"
                            color={f.priority >= 10 ? 'success' : 'default'}
                            variant={f.priority >= 10 ? 'filled' : 'outlined'}
                          />
                        ))
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Switch checked={s.active} onChange={() => handleToggleActive(s)} size="small" />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" startIcon={<EditIcon />} onClick={() => handleOpenEdit(s)}>Edit</Button>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={7} sx={{ p: 0, borderBottom: expanded[s.schema_key] ? undefined : 'none' }}>
                    <Collapse in={!!expanded[s.schema_key]} unmountOnExit>
                      <FamiliesPanel
                        schemaKey={s.schema_key}
                        families={s.applicable_families || []}
                        allFamilies={families}
                        onChange={load}
                        onReclassify={triggerReclassify}
                      />
                      <CurvesPanel
                        schemaKey={s.schema_key}
                        curves={schemaCurves}
                        onChange={load}
                      />
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Schema' : 'Add Schema'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Schema Key"
            value={formData.schema_key}
            onChange={(e) => setFormData({ ...formData, schema_key: e.target.value })}
            disabled={!!editing}
            fullWidth margin="normal"
            helperText="Lowercase identifier, e.g. alpha, eu_numeric"
          />
          <TextField label="Label" value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })} fullWidth margin="normal" />
          <TextField label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} fullWidth margin="normal" multiline rows={2} />
          <TextField
            label="Example Sizes (comma-separated)"
            value={formData.example_sizes}
            onChange={(e) => setFormData({ ...formData, example_sizes: e.target.value })}
            fullWidth margin="normal"
            helperText="e.g. XS, S, M, L, XL"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !formData.label || (!editing && !formData.schema_key)}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
